import {
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
} from '@mui/material';
import { useStyles } from './styles/ReportTable.styles';
import type { SortDirection } from '@api/cut';
import { CutReportTableHeader } from '@components/CutReportTableHeader';
import type { CutTableOptions } from '@/types/table';
import React, { isValidElement } from 'react';

interface ReportTableProps {
    reportName: string;
    tableOptions: CutTableOptions;
    loading?: boolean;
    height: number | string;
    sortOptions?: Record<string, SortDirection>;
    setSortDirection?: (key: string, value: SortDirection) => void;
}

export const CutReportTable = ({
    tableOptions,
    loading = false,
    height,
    sortOptions,
    setSortDirection,
}: ReportTableProps) => {
    const classes = useStyles();

    return (
        <Paper elevation={0} className={classes.wrapper}>
            {loading || (tableOptions.data.length !== 0 && tableOptions.data.length === 0) ? (
                <div className={classes.loadingIndicator} style={{ minHeight: height }}>
                    <CircularProgress />
                </div>
            ) : (
                <TableContainer className={classes.container} sx={{ minHeight: height }}>
                    <Table stickyHeader aria-label='sticky table'>
                        <TableHead>
                            <TableRow>
                                {tableOptions.headers.map((header: string, index: number) => {
                                    const headerFormatted = header.replaceAll(/ /g, '').toLowerCase();
                                    const sortDirection = sortOptions?.[headerFormatted];

                                    return (
                                        <CutReportTableHeader
                                            key={`header - Cuts - ${header} - ${index}`}
                                            showSort={Boolean(sortDirection)}
                                            header={header}
                                            sortDirection={sortDirection}
                                            onSort={(direction) => setSortDirection?.(headerFormatted, direction)}
                                        />
                                    );
                                })}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {tableOptions.data.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={tableOptions.headers.length}
                                        height={35}
                                        style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                        There is no data to show
                                    </TableCell>
                                </TableRow>
                            )}

                            {tableOptions.data.map((row: (string | number | React.ReactNode)[], idx: number) => {
                                const header = tableOptions.headers[idx];

                                return (
                                    <TableRow key={`row - Cuts - ${header} - ${idx}`} className={classes.tableRow}>
                                        {row.map((item: string | number | React.ReactNode, idx: number) => {
                                            const colorFn = tableOptions.color?.[idx];

                                            return isValidElement(row) ? (
                                                <TableCell
                                                    sx={{ minWidth: '150px' }}
                                                    key={`Cuts - ${header} - ${item} - ${idx}`}
                                                    align='left'>
                                                    {item}
                                                </TableCell>
                                            ) : (
                                                <TableCell
                                                    key={`Cuts - ${header} - ${item} - ${idx}`}
                                                    align='left'
                                                    sx={{
                                                        minWidth: '150px',
                                                        color: colorFn ? colorFn(item as string | number) : 'default',
                                                    }}>
                                                    {item}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>

                        <TableFooter className={classes.tableFooter}>
                            <TableRow></TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
};
